import { CssCalc } from './../Helpers/CssCalc.js';

export class ScrollAnimation {
	constructor(container, element) {
		if (typeof container === 'undefined' || typeof element === 'undefined') {
			return false;
		}

		this.container = $(container).get(0);
		this.element = $(element).get(0);

		if (!this.container || !this.element) {
			return false;
		}

		$(this.element).addClass('scroll-animation-element');

		// Config / measurements
		this.proxyScrollHeight = 0;
		this.minWidth = 1;
		this.maxWidth = 1;
		this.elementOffset = 0;
		this.elementHeight = 0;

		// State
		this.scrollProxy = 0;
		this.additionalScroll = 0;
		this.lastScrollY = window.scrollY || 0;
		this.isSyncing = false;
		this.inView = true; // optimistic until observed
		this.pendingRaf = null;

		// Observers
		this.io = null;
		this.ro = null;

		// Bind handlers
		this.onWheel = this.onWheel.bind(this);
		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.onResize = this.onResize.bind(this);
		this.onScroll = this.onScroll.bind(this);

		// Touch state
		this.lastTouchY = null;

		// Init
		this.init();
	}

	// Utils
	clamp(val, min, max) {
		return Math.max(min, Math.min(max, val));
	}

	isFancyboxOpen() {
		return document.documentElement.classList.contains('with-fancybox');
	}

	isElementInViewport() {
		// If IntersectionObserver tracked, use cached inView
		if (this.io) return this.inView;
		const rect = this.element.getBoundingClientRect();
		const vh = window.innerHeight || document.documentElement.clientHeight;
		return rect.top < vh && rect.bottom > 0;
	}

	measureConfig() {
		const styles = getComputedStyle(this.container);
		// Read CSS variables with fallbacks
		let heightVar = styles.getPropertyValue('--scroll-animation-height').trim();
		let minMaxWidthVar = styles.getPropertyValue('--scroll-animation-min-max-width').trim();
		let minPaddingVar = styles.getPropertyValue('--scroll-animation-min-padding').trim();

		if (!heightVar) {
			// data attribute fallback
			heightVar = this.container.getAttribute('data-scroll-animation-height') || '0px';
		}
		if (!minMaxWidthVar) {
			minMaxWidthVar = this.container.getAttribute('data-scroll-animation-min-width') || '0px';
		}
		if (!minMaxWidthVar) {
			minPaddingVar = this.container.getAttribute('data-scroll-animation-min-padding') || '40px';
		}

		// Convert to px
		const minMaxWidthPx = CssCalc.toPx(minMaxWidthVar || '0px') || 0;
		const minPaddingPx = CssCalc.toPx(minPaddingVar || '0px') || 0;

		// Outer width (inkl. padding + border)
		const containerWidth = this.container.getBoundingClientRect().width;

		// minWidth = min( containerWidth - minPadding, minMaxWidth )
		this.minWidth = Math.min(
			Math.max(containerWidth - minPaddingPx, 0),
			minMaxWidthPx
		);

		this.proxyScrollHeight = CssCalc.toPx(heightVar || '0px') || 0;
	}

	measureLayout() {
		this.maxWidth = $(this.container).outerWidth() || 1;
		const offset = $(this.element).offset();
		this.elementOffset = offset ? offset.top : 0;
		this.elementHeight = this.element.offsetHeight || 0;
	}

	readConfig() {
		this.measureConfig();
		this.measureLayout();
	}

	computeProgress() {
		if (!this.proxyScrollHeight) return 0;
		return this.clamp(this.scrollProxy / this.proxyScrollHeight, 0, 1);
	}

	applyStyles(progress) {
		// Set CSS var for external styling
		this.container.style.setProperty('--scroll-animation-proxy-progress', `${progress * 100}%`);
		// Scale
		if (this.maxWidth <= this.minWidth) {
			this.minWidth = this.maxWidth;
		}
		const scale = 1 + ((this.minWidth / this.maxWidth) - 1) * progress;
		this.element.style.transform = `scale(${scale})`;
		// Adjust window scroll to compensate for virtual gap (only when not syncing)
		this.adjustScrollForVirtualGap(scale, progress);
	}

	requestUpdate() {
		if (this.pendingRaf) return;
		this.pendingRaf = requestAnimationFrame(() => {
			this.pendingRaf = null;
			const progress = this.computeProgress();
			this.applyStyles(progress);
		});
	}

	update() {
		this.requestUpdate();
	}

	syncWithScroll() {
		if (!this.container || !this.element) return;
		this.readConfig();
		const y = window.scrollY || window.pageYOffset || 0;
		const pastTop = Math.max(0, y - this.elementOffset);
		if (this.proxyScrollHeight > 0) {
			const progress = this.clamp(pastTop / this.proxyScrollHeight, 0, 1);
			this.scrollProxy = progress * this.proxyScrollHeight;
			this.isSyncing = true;
			this.update();
			this.isSyncing = false;
		} else {
			this.scrollProxy = 0;
			this.update();
		}
	}

	adjustScrollForVirtualGap(scale, progress) {
		const currentGap = this.elementHeight * (1 - scale);
		const maskHeight = this.proxyScrollHeight * progress;
		const scrollY = Math.max(0, currentGap - maskHeight);
		if (scrollY > 0) {
			this.additionalScroll = scrollY;
			if (this.isSyncing) return;
			window.scrollTo({ top: scrollY, left: 0, behavior: 'smooth' });
		}
	}

	handleDelta(deltaY, e) {
		if (this.isFancyboxOpen()) return;
		if (!this.proxyScrollHeight) return;

		const threshold = 200 + Math.abs(deltaY) * 0.3;
		const currentScrollY = this.lastScrollY;
		const nextScrollY = currentScrollY + deltaY;
		const direction = Math.sign(deltaY);
		if (direction === 0) return;

		const inView = this.isElementInViewport();

		const canScrollDownProxy = direction > 0 && inView && this.scrollProxy < this.proxyScrollHeight;
		const canScrollUpProxy = direction < 0 && inView && this.scrollProxy > 0 &&
			(nextScrollY - this.additionalScroll) <= this.elementOffset + threshold;

		if (canScrollDownProxy || canScrollUpProxy) {
			e && e.preventDefault && e.preventDefault();
			this.scrollProxy = this.clamp(this.scrollProxy + deltaY, 0, this.proxyScrollHeight);
			this.update();
		}
	}

	onWheel(e) {
		const deltaY = (e.deltaY || 0) / 3;
		this.handleDelta(deltaY, e);
	}

	onTouchStart(e) {
		if (e.touches && e.touches.length > 0) {
			this.lastTouchY = e.touches[0].clientY;
		}
	}

	onTouchMove(e) {
		if (!this.proxyScrollHeight) return;
		if (!this.isElementInViewport()) return;
		if (e.touches && e.touches.length > 0 && this.lastTouchY !== null) {
			const currentY = e.touches[0].clientY;
			const deltaY = (this.lastTouchY - currentY);
			this.lastTouchY = currentY;
			this.handleDelta(deltaY, e);
		}
	}

	onTouchEnd() {
		this.lastTouchY = null;
	}

	attachObservers() {
		if ('IntersectionObserver' in window) {
			this.io = new IntersectionObserver((entries) => {
				for (const entry of entries) {
					if (entry.target === this.element) {
						this.inView = entry.isIntersecting;
					}
				}
			}, { root: null, threshold: [0, 0.01, 0.99, 1] });
			this.io.observe(this.element);
		}
		if ('ResizeObserver' in window) {
			this.ro = new ResizeObserver(() => {
				// Only re-measure and sync; apply via RAF
				this.measureLayout();
				this.update();
			});
			this.ro.observe(this.container);
			this.ro.observe(this.element);
		}
	}

	detachObservers() {
		if (this.io) {
			this.io.disconnect();
			this.io = null;
		}
		if (this.ro) {
			this.ro.disconnect();
			this.ro = null;
		}
	}

	onResize() {
		CssCalc.refresh && CssCalc.refresh();
		this.readConfig();
		this.syncWithScroll();
	}

	onScroll() {
		this.lastScrollY = window.scrollY || 0;
	}

	attachListeners() {
		this.container.addEventListener('wheel', this.onWheel, { passive: false });
		// Enable touch handling with proper passive flags
		this.container.addEventListener('touchstart', this.onTouchStart, { passive: true });
		this.container.addEventListener('touchmove', this.onTouchMove, { passive: false });
		this.container.addEventListener('touchend', this.onTouchEnd, { passive: true });
		this.container.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
		window.addEventListener('resize', this.onResize);
		window.addEventListener('scroll', this.onScroll);
	}

	detachListeners() {
		this.container.removeEventListener('wheel', this.onWheel);
		this.container.removeEventListener('touchstart', this.onTouchStart);
		this.container.removeEventListener('touchmove', this.onTouchMove);
		this.container.removeEventListener('touchend', this.onTouchEnd);
		this.container.removeEventListener('touchcancel', this.onTouchEnd);
		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('scroll', this.onScroll);
	}

	init() {
		this.readConfig();
		this.syncWithScroll();
		this.attachObservers();
		this.attachListeners();
	}

	destroy() {
		this.detachListeners();
		this.detachObservers();
		if (this.pendingRaf) {
			cancelAnimationFrame(this.pendingRaf);
			this.pendingRaf = null;
		}
		// Optional: clear styles set by the component
		this.container.style.removeProperty('--scroll-animation-proxy-progress');
		this.element.style.removeProperty('transform');
	}
}